import React, { CSSProperties, useMemo } from "react";
import styled, { useTheme } from "styled-components";
import Flex from "common/Boxes/Flex";
import Card from "common/Boxes/Card";
import { motion } from "motion/react";
import { v4 as uuidv4 } from "uuid";
import { usePageSize } from "common/Home/PageWrapper/PageSizeProvider.tsx";
import { STATS_AND_CHART_CARD_ID } from "testIds.ts";

interface StatsAndChartCardProps {
  topStatistic?: React.JSX.Element;
  bottomStatistic?: React.JSX.Element;
  chart?: React.JSX.Element;
  expanded: boolean;
  setExpanded: (expanded: boolean) => void;
  statisticsStyle?: CSSProperties;
  style?: CSSProperties;
  animateStatisticStyle?: CSSProperties;
  expandedHeight?: number;
  collapsedHeight?: number;
}

const StatsAndChartCard = ({
  topStatistic,
  bottomStatistic,
  chart,
  expanded,
  setExpanded,
  statisticsStyle,
  style,
  animateStatisticStyle,
  expandedHeight,
  collapsedHeight = 100,
}: StatsAndChartCardProps) => {
  const { width: pageWidth } = usePageSize();

  const expandedChartWidth = pageWidth ? pageWidth - PADDING * 2 : undefined;
  const collapsedChartWidth = expandedChartWidth
    ? expandedChartWidth * 0.55
    : undefined;
  const width = expanded ? expandedChartWidth : collapsedChartWidth;
  const height = expanded ? expandedHeight : collapsedHeight;

  const gradient = (
    <motion.div
      initial={false}
      animate={{ opacity: expanded ? 0 : 1 }}
      transition={{
        delay: 0.2,
        ease: "linear",
        duration: 0.1,
      }}
    >
      {!expanded && <Gradient />}
    </motion.div>
  );

  return (
    <StyledCard
      data-testid={STATS_AND_CHART_CARD_ID}
      onClick={(event) => {
        if (event.target instanceof HTMLCanvasElement) return;
        setExpanded(!expanded);
      }}
      style={style}
    >
      <ContentGraphWrapper $expanded={expanded}>
        <ContentWrapper $expanded={expanded} style={statisticsStyle}>
          <TopStatisticWrapper>{topStatistic}</TopStatisticWrapper>
          <AnimatedStatisticWrapper style={animateStatisticStyle}>
            <motion.div layout>
              <BottomStatisticWrapper $expanded={expanded}>
                {bottomStatistic}
              </BottomStatisticWrapper>
            </motion.div>
          </AnimatedStatisticWrapper>
        </ContentWrapper>
        <motion.div
          initial={false}
          layout={true}
          style={{ position: "relative" }}
          animate={{ width, height }}
          transition={{ type: "tween" }}
        >
          {gradient}
          {chart}
        </motion.div>
      </ContentGraphWrapper>
    </StyledCard>
  );
};

export default StatsAndChartCard;

const PADDING = 16;
const Gradient = (props: React.SVGProps<SVGSVGElement>) => {
  const theme = useTheme();
  const stopColor = theme.isNewLook
    ? theme.colors.cardBgColor
    : theme.isDarkMode
    ? "#2a79e7"
    : "#2c6bf6";
  const prefix = useMemo(() => uuidv4(), []);
  return (
    <svg
      {...props}
      style={{
        position: "absolute",
        top: -18,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1,
      }}
    >
      <defs>
        <linearGradient id={`${prefix}blueGrad`} x1="0" y1="0" x2="100%" y2="0">
          <stop offset="0%" stopColor={stopColor} stopOpacity={0.9} />
          <stop offset="10%" stopColor={stopColor} stopOpacity={0.2} />
          <stop offset="13%" stopColor={stopColor} stopOpacity={0} />
        </linearGradient>
      </defs>
      <rect
        x="0"
        y="0"
        width="100%"
        height="200"
        fill={`url(#${prefix}blueGrad)`}
        clipPath="url(#clip)"
      />
    </svg>
  );
};

const StyledCard = styled(Card)`
  background: ${(p) =>
    p.theme.isDarkMode
      ? "linear-gradient(268.79deg, #70A8FF -8.96%, #0054D6 101.75%)"
      : "linear-gradient(264.62deg, #3272fe 34.96%, #205fe8 99.12%)"};
  border-radius: 12px;
  cursor: pointer;
  padding: 16px 0;
  overflow: hidden;
  ${(p) =>
    p.theme.isNewLook &&
    `
    background: ${p.theme.colors.cardBgColor};
    border: ${p.theme.borders.card};
    border-radius: ${p.theme.radii.card}px;
    box-shadow: none;
  `}
`;
const ContentGraphWrapper = styled(Flex)<{ $expanded?: boolean }>`
  align-items: flex-start;
  flex-direction: ${(p) => (p.$expanded ? "column" : "row")};
`;
const ContentWrapper = styled(Flex)<{ $expanded?: boolean }>`
  padding: 0 16px;
  width: ${(p) => (p.$expanded ? "100%" : "45%")};
  align-items: ${(p) => (p.$expanded ? "flex-end" : "flex-start")};
  flex-direction: ${(p) => (p.$expanded ? "row" : "column")};
`;
const TopStatisticWrapper = styled.div`
  width: 100%;
`;
const BottomStatisticWrapper = styled.div<{ $expanded?: boolean }>`
  margin-top: ${(p) => (p.$expanded ? 0 : 8)}px;
`;
const AnimatedStatisticWrapper = styled(Flex)`
  width: 100%;
  justify-content: flex-start;
`;
